import { useDispatch } from 'react-redux'
import { useMemo, useRef } from 'react'
import { Callback } from 'Src/types/utilTypes'
import { ActionsObjectResult } from 'Src/types/actionsObjectResult'

export type ActionsReturn<T extends ActionsObjectResult<Callback>> = {
	[Key in keyof T]: (...args: Parameters<T[Key]>) => void
}
/**
 * @example Example usage of useAppDispatch.
 * // instead of
 * //  const dispatch = useDispatch()
 * //  const createSmsRule =  (	data: SmsRule, callback?: Callback)=>dispatch(createSmsRuleAction(data,callback))
 * const actions = {
 * 	 createSmsRule: createSmsRuleAction,
 * }
 * const { createSmsRule } = useAppDispatch(actions)
 * @returns {Object} memoized object with actions
 */
export const useAppDispatch = <T extends ActionsObjectResult<Callback<any>>>(
	actions: T
): ActionsReturn<T> => {
	const actionsRef = useRef(actions)
	actionsRef.current = actions

	const dispatch = useDispatch()

	return useMemo(() => {
		return Object.entries(actionsRef.current).reduce((acc, [key, fn]: [keyof T, Callback]) => {
			// @ts-ignore
			acc[key] = (...args) => dispatch(fn(...args))
			return acc
		}, {} as ActionsReturn<T>)
	}, [dispatch])
}
